import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { Autocomplete } from '@mui/material';
import { AutocompleteRenderInputParams } from '@mui/material/Autocomplete';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';

import BaseInput from 'components/common/BaseInput';
import HelperDesc from 'components/common/WarningInputMessage/WarningInputMessage';
import {
    Actions,
    FieldGroup,
    Form,
    Modal,
    ModalHeader,
    PrimaryButton,
    SecondaryButton,
    Section,
} from 'components/common/Modal';

import { RootState } from 'redux/store';

import { useTranslation } from 'react-i18next';
import useEnabledCountries from 'hooks/useEnabledCountries';
import { chosenCountryName } from 'utils/countries';

type LanguageOption = {
    value: string;
    label: string;
};

interface Case {
    country: string;
    language: LanguageOption | null;
}

type Props = {
    open: boolean;
    closeModal: () => void;
    handleDownloadReport: (arg1: string, arg2: string, arg3: string) => Promise<void> | void;
};

const AssessmentDownloadModal = ({ open, closeModal, handleDownloadReport }: Props) => {
    const { id: candidateID } = useParams();
    const { language_setting: language } = useSelector((state: RootState) => state.user.entity);
    const { codes, names: countryOptions } = useEnabledCountries();
    const { t } = useTranslation();
    const languageOptions: LanguageOption[] = [
        { value: 'EN', label: 'English' },
        { value: 'FR', label: 'French' },
    ];
    const [isLoading, setIsLoading] = useState(false);

    const {
        control,
        formState: { errors, isDirty },
        handleSubmit,
    } = useForm<Case>({
        mode: 'all',
        defaultValues: {
            country: chosenCountryName(codes) ?? '',
            language: null,
        },
    });

    const submitForm: SubmitHandler<Case> = async (data) => {
        try {
            setIsLoading(true);
            if (candidateID && data.language?.value) {
                await handleDownloadReport(candidateID, data.country, data.language.value);
            }

            closeModal();
        } catch (error) {
            console.error('Error downloading report:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const activeBtnCondition = isDirty && Object.keys(errors).length === 0;

    return (
        <Modal
            open={open}
            onClose={closeModal}
            size="sm"
            ariaLabel={t('assessmentReport', {
                lng: language,
                defaultValue: 'Assessment report',
            })}
        >
            <ModalHeader
                eyebrow={t('assessment', { lng: language, defaultValue: 'Assessment' })}
                title={t('assessmentReport', {
                    lng: language,
                    defaultValue: 'Assessment report',
                })}
                subtitle={t('assessmentReportDownloadSubtitle', {
                    lng: language,
                    defaultValue: 'Choose the destination country and report language.',
                })}
            />

            <Form onSubmit={handleSubmit(submitForm)} style={{ marginTop: '16px' }}>
                <Section>
                    <FieldGroup>
                        <Controller
                            control={control}
                            name={'country'}
                            rules={{ required: t('requiredField', { lng: language }) }}
                            render={({
                                field: { onChange, onBlur, value },
                                fieldState: { error },
                            }) => (
                                <Autocomplete
                                    id="country"
                                    options={countryOptions}
                                    disablePortal
                                    value={value || null}
                                    onBlur={onBlur}
                                    renderOption={(props, option) => (
                                        <li {...props} style={{ margin: 0 }}>
                                            {option}
                                        </li>
                                    )}
                                    onChange={(_, data) => {
                                        onChange(data as string);
                                    }}
                                    renderInput={(params) => (
                                        <BaseInput
                                            {...params}
                                            label={t('country', { lng: language })}
                                            error={!!error}
                                            helperText={
                                                error ? (
                                                    <HelperDesc
                                                        message={error.message?.toString()}
                                                    />
                                                ) : (
                                                    ''
                                                )
                                            }
                                        />
                                    )}
                                />
                            )}
                        />
                    </FieldGroup>

                    <FieldGroup>
                        <Controller
                            control={control}
                            name={'language'}
                            rules={{ required: t('requiredField', { lng: language }) }}
                            render={({
                                field: { onChange, onBlur, value },
                                fieldState: { error },
                            }) => (
                                <Autocomplete
                                    options={languageOptions}
                                    isOptionEqualToValue={(option, value) =>
                                        option.value === value?.value
                                    }
                                    fullWidth
                                    disablePortal
                                    id="language"
                                    value={value || null}
                                    getOptionLabel={(option) => option.label}
                                    onBlur={onBlur}
                                    onChange={(_, data) => {
                                        onChange(data);
                                    }}
                                    renderOption={(props, option) => (
                                        <li {...props} key={option.value} style={{ margin: 0 }}>
                                            {option.label}
                                        </li>
                                    )}
                                    renderInput={(params: AutocompleteRenderInputParams) => (
                                        <BaseInput
                                            {...params}
                                            label={t('language', { lng: language })}
                                            error={!!error}
                                            helperText={
                                                error ? (
                                                    <HelperDesc
                                                        message={error.message?.toString()}
                                                    />
                                                ) : (
                                                    ''
                                                )
                                            }
                                        />
                                    )}
                                />
                            )}
                        />
                    </FieldGroup>
                </Section>

                <Actions>
                    <SecondaryButton type="button" onClick={closeModal}>
                        {t('cancel', { lng: language, defaultValue: 'Cancel' })}
                    </SecondaryButton>
                    <PrimaryButton type="submit" disabled={!activeBtnCondition || isLoading}>
                        {isLoading
                            ? t('downloading', { lng: language, defaultValue: 'Downloading...' })
                            : t('download', { lng: language })}
                    </PrimaryButton>
                </Actions>
            </Form>
        </Modal>
    );
};

export default AssessmentDownloadModal;
